import { useParams } from 'react-router-dom';
import { useState, Fragment, useContext, useEffect } from 'react';
import { useGet } from '@/query/fetchHooks';
import { InputText, FilterDropdown, TEOLoader } from '@teo/components';
import { Search } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { PublicationTable } from '@/components/PublicationTable';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { NavContext } from '@/components/NavContext';
import { Userwithorganisationwithidps } from '@/models';
import { newUid, useMobile } from '@/util';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';

const PublicationCourses = ({
  title,
  courseType,
}: {
  courseType: string;
  title: string;
}) => {
  const isMobile = useMobile();
  const [filter, setFilter] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [listCategories, setListCategories] = useState<any>([]);
  const [category, setCategory] = useState<any>();
  const [courseScope, setCourseScope] = useState<'assigned' | 'all'>(
    'assigned'
  );

  const { organisationId } = useParams();
  const { t } = useTranslation();
  const coursePath = window.location.pathname;

  const { auth } = useContext(AuthContext);
  const { setSubPage } = useContext(NavContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));

  const [loader, setLoader] = useState(true);

  const courseScopeData = [
    {
      id: 'assigned',
      name: t('pages.follow_up_courses.assigned_courses'),
    },
    {
      id: 'all',
      name: t('pages.follow_up_courses.all_courses'),
    },
  ];

  const selectedCourseScope = courseScopeData.find(
    (option) => option.id === courseScope
  );

  const allCategories: any = useGet(`/categories`, {
    count: 1000,
    sort: 'tag',
    lang: auth?.user?.lang,
    organisationId: `${auth?.user?.activeOrganisationId},OR,organisationId=null`,
  });

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useEffect(() => {
    if (allCategories?.data) {
      const data = (allCategories?.data?.data as any[]).map((tag) => ({
        id: tag?.id,
        name: tag?.tag,
      }));
      setListCategories(data);
    }
  }, [allCategories?.data]);

  useEffect(() => {
    const myFilter = filterInput + ' ' + (category?.name ? category?.name : '');
    setFilter(myFilter);
  }, [filterInput, category]);

  return (
    <div className="w-full max-w-6xl pb-8">
      {isMobile ? (
        <MobileSubPageMenu title={title} />
      ) : (
        <div className="my-8 flex justify-between">
          <h1 className="text-xl font-semibold text-black">{t(title)}</h1>
        </div>
      )}

      <Fragment>
        <div className="pb-8 sm:w-2/3 md:w-1/2">
          <InputText
            value={filterInput}
            onChange={(ev) => setFilterInput(ev.target.value)}
            Icon={Search}
            placeholder={t('search.course')}
          />
        </div>

        <div className="mb-8 flex flex-col sm:flex-row">
          <span className="mb-4 pr-2 sm:mb-0">
            <FilterDropdown
              clearButtonLabel="clear"
              onChange={(value: any) => {
                setCategory(value);
              }}
              buttonText={`${t('pages.follow_up.all_categories_tag')}`}
              options={listCategories}
              searchable={true}
              value={category}
            />
          </span>

          {courseType !== 'instruction' && (
            <span className="pr-2">
              <FilterDropdown
                clearButtonLabel="clear"
                onChange={(value: any) => {
                  setCourseScope(
                    (value?.id || 'assigned') as 'assigned' | 'all'
                  );
                  setLoader(true);
                  setRefetchTable(newUid(20));
                }}
                buttonText={
                  selectedCourseScope?.name ||
                  t('pages.follow_up_courses.assigned_courses')
                }
                options={courseScopeData}
                value={
                  courseScope === 'assigned' ? undefined : selectedCourseScope
                }
                dropdownPositionMobileRight={true}
              />
            </span>
          )}
        </div>

        {loader && (
          <div className="mx-auto flex w-full justify-center py-20 px-10">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        )}

        <div className={`w-full ${loader === true && ' hidden '}`}>
          <PublicationTable
            courseType={courseType}
            filter={filter}
            rowPath={(course: any) =>
              courseType === 'instruction'
                ? `${coursePath}/details/${course.id}`
                : `${coursePath}/${course.id}`
            }
            key={refetchTable}
            fetch={
              courseType === 'instruction'
                ? useGet.bind(null, '/myteachedcourses', {
                    userId: authUser?.id,
                    organisationId: authUser?.activeOrganisationId,
                    courseType: courseType,
                    publicationStatus:
                      'published,OR,publicationStatus=concept,OR,publicationStatus=template',
                    onSuccess: () => {
                      setLoader(false);
                    },
                    onError: (res: any) => {
                      console.error('res', res);
                      setLoader(false);
                    },
                  })
                : courseScope === 'all'
                ? useGet.bind(null, '/myactivecoursecontents', {
                    userId: authUser?.id,
                    masterId: null,
                    publicationCourseType: courseType,
                    publicationStatus:
                      'published,OR,publicationStatus=concept,OR,publicationStatus=template',
                    onSuccess: () => {
                      setLoader(false);
                    },
                    onError: (res: any) => {
                      console.error('res', res);
                      setLoader(false);
                    },
                  })
                : useGet.bind(null, '/myusedpublications', {
                    userId: authUser?.id,
                    organisationId: organisationId,
                    publicationCourseType: courseType,
                    publicationStatus:
                      'published,OR,publicationStatus=concept,OR,publicationStatus=template',
                    onSuccess: () => {
                      setLoader(false);
                    },
                    onError: (res: any) => {
                      console.error('res', res);
                      setLoader(false);
                    },
                  })
            }
            empty={{
              template: 2,
              text: t('empty_tenplate.course_list'),
            }}
          />
        </div>
      </Fragment>
    </div>
  );
};

export default PublicationCourses;
